// src/utils/graphqlFetch.ts
// Resilient GraphQL fetch wrapper with automatic token refresh and 401 retry.
//
// Replaces raw fetch(GRAPHQL_API_URL, ...) calls across the app so that:
//  1. Token expiry is checked before every request; if within 2-min window, refresh first.
//  2. Authorization header is added automatically.
//  3. On 401 response, refreshAccessToken() is called once and the request is retried.
//  4. On retry failure, auth state is cleared and the user is redirected to /login.

import { getAuthToken } from './cookieUtils';
import {
  refreshAccessToken,
  shouldRefreshToken,
  clearAuthState,
} from '../services/authService';
import { GRAPHQL_API_URL } from '../constants';

// Mutex to prevent concurrent refresh attempts across parallel requests
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

async function ensureFreshToken(): Promise<void> {
  if (!shouldRefreshToken()) return;

  // If a refresh is already in flight, wait for it
  if (isRefreshing && refreshPromise) {
    await refreshPromise;
    return;
  }

  isRefreshing = true;
  refreshPromise = refreshAccessToken();
  try {
    await refreshPromise;
  } finally {
    isRefreshing = false;
    refreshPromise = null;
  }
}

/**
 * Execute a GraphQL query/mutation with automatic auth handling.
 *
 * Returns the parsed JSON body (`{ data, errors }`).
 * On unrecoverable auth failure the browser is redirected to /login.
 */
export async function graphqlFetch(
  query: string,
  variables?: Record<string, any>,
): Promise<any> {
  // --- Pre-request: proactively refresh if token is about to expire ---
  try {
    await ensureFreshToken();
  } catch {
    // If proactive refresh fails we still attempt the request — the token
    // may still be valid for a few more seconds.
  }

  const doRequest = async (): Promise<Response> => {
    const token = getAuthToken();
    return fetch(GRAPHQL_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ query, variables }),
    });
  };

  let response = await doRequest();

  // --- 401 retry: refresh once, then retry the request ---
  if (response.status === 401) {
    try {
      // Coordinate with other in-flight refreshes
      if (isRefreshing && refreshPromise) {
        await refreshPromise;
      } else {
        isRefreshing = true;
        refreshPromise = refreshAccessToken();
        try {
          await refreshPromise;
        } finally {
          isRefreshing = false;
          refreshPromise = null;
        }
      }

      response = await doRequest();
    } catch {
      // Refresh failed — bail out
    }

    // If still 401 after retry, redirect to login
    if (response.status === 401) {
      clearAuthState();
      window.location.href = '/login';
      // Return a minimal object so callers don't crash before redirect completes
      return { data: null, errors: [{ message: 'Session expired' }] };
    }
  }

  return response.json();
}

export { GRAPHQL_API_URL };
